import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Mail, Lock, AlertCircle, User as UserIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodI18nResolver } from '../lib/validation';
import { registerDataSchema, type RegisterData } from '../schemas/auth';
import Auth3DLayout from '../components/auth3d/Auth3DLayout';
import GlassPanel from '../components/auth3d/GlassPanel';
import AuthInput from '../components/auth3d/AuthInput';
import AuthButton from '../components/auth3d/AuthButton';

const RegisterPage: React.FC = () => {
  const { register: registerAuth, isAuthenticated, isLoading, error } = useAuth();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    clearErrors: clearFormErrors,
  } = useForm<RegisterData>({
    resolver: zodI18nResolver(registerDataSchema),
  });

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to='/' replace />;
  }

  const onSubmit = async (data: RegisterData) => {
    clearFormErrors(['username', 'email', 'password', 'confirm_password']);

    try {
      const result = await registerAuth({
        username: data.username,
        email: data.email,
        password: data.password,
        confirm_password: data.confirm_password,
      });

      if (!result.success) {
        // Error is set by AuthContext
      }
    } catch (err) {
      // Error is set by AuthContext
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Auth3DLayout>
      <GlassPanel delay={0.5}>
        <div className='p-8 sm:p-10 space-y-8'>
          {/* Title */}
          <div className='text-center space-y-4'>
            <div className='space-y-2'>
              <h1
                className='text-3xl sm:text-4xl font-bold text-white'
                style={{ fontFamily: 'Orbitron, sans-serif' }}
              >
                {t('auth.createAccount')}
              </h1>
              <p className='text-gray-400 text-sm'>
                {t('auth.createAccountSubtitle')}{' '}
                <Link
                  to='/login'
                  className='text-cyan-400 hover:text-cyan-300 transition-colors font-medium'
                  data-testid='login-link'
                >
                  {t('auth.signInToExisting')}
                </Link>
              </p>
            </div>
          </div>

          {/* Form */}
          <form
            className='space-y-4'
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            data-testid='register-form'
          >
            {/* Name Field */}
            <AuthInput
              id='username'
              label={t('auth.fullName')}
              type='text'
              placeholder={t('auth.enterFullName')}
              icon={UserIcon}
              error={errors.username?.message}
              {...register('username')}
              autoComplete='name'
              data-testid='register-name-input'
            />

            {/* Email Field */}
            <AuthInput
              id='email'
              label={t('auth.email')}
              type='email'
              placeholder={t('auth.enterEmail')}
              icon={Mail}
              error={errors.email?.message}
              {...register('email')}
              autoComplete='email'
              data-testid='register-email-input'
            />

            {/* Password Field */}
            <div className='relative'>
              <AuthInput
                id='password'
                label={t('auth.password')}
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.createPassword')}
                icon={Lock}
                error={errors.password?.message}
                {...register('password')}
                autoComplete='new-password'
                data-testid='register-password-input'
                className='pr-12'
              />
              <button
                type='button'
                className='absolute right-3 top-[2.6rem] text-gray-400 hover:text-cyan-400 transition-colors'
                onClick={togglePasswordVisibility}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* Confirm Password Field */}
            <div className='relative'>
              <AuthInput
                id='confirm_password'
                label={t('auth.confirmPassword')}
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={t('auth.confirmYourPassword')}
                icon={Lock}
                error={errors.confirm_password?.message}
                {...register('confirm_password')}
                autoComplete='new-password'
                data-testid='register-confirm-password-input'
                className='pr-12'
              />
              <button
                type='button'
                className='absolute right-3 top-[2.6rem] text-gray-400 hover:text-cyan-400 transition-colors'
                onClick={toggleConfirmPasswordVisibility}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* Error Display */}
            {error && (
              <div className='rounded-lg bg-red-500/10 border border-red-500/30 p-4 backdrop-blur-sm'>
                <div className='flex gap-3'>
                  <AlertCircle className='h-5 w-5 text-red-400 flex-shrink-0 mt-0.5' />
                  <p className='text-sm text-red-300'>{error}</p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <AuthButton
              type='submit'
              isLoading={isSubmitting || isLoading}
              loadingText={t('auth.creatingAccount')}
              data-testid='register-submit-button'
              className='mt-6'
            >
              {t('auth.signUp')}
            </AuthButton>

            {/* Terms and Conditions */}
            <div className='text-xs text-gray-400 text-center'>
              {t('auth.byCreatingAccount')}{' '}
              <Link to='/terms' className='text-cyan-400 hover:text-cyan-300 transition-colors'>
                {t('auth.termsOfService')}
              </Link>{' '}
              {t('auth.and')}{' '}
              <Link to='/privacy' className='text-cyan-400 hover:text-cyan-300 transition-colors'>
                {t('auth.privacyPolicy')}
              </Link>
            </div>
          </form>
        </div>
      </GlassPanel>
    </Auth3DLayout>
  );
};

export default RegisterPage;
